import {
  Input,
  Typography,
  Button,
  Controller,
  Delimiter,
  useForm,
  BottomNavigation,
  PageModal,
  FormDirtyModal,
} from '@otp/ui';
import { List } from '@otp/icons';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  CreateWttpQuestionnaireDocument,
  UpdateWttpQuestionnaireDocument,
  WttpQuestionnaireQuestionDto,
} from 'src/graphql/generated/graphql';
import 'react-quill/dist/quill.snow.css';

import styles from './index.module.css';
import { DocumentNode, useMutation } from '@apollo/client';
import { WttpQuestionsListForm } from './questions-list';
import { useEffect, useState } from 'react';
import { useQuestionnaire } from 'src/hooks/query/use-questionnaire';

type Form = {
  name: string;
  questions: WttpQuestionnaireQuestionDto[];
};

interface QuestionnaireFormProps {
  onFinish?: (id: string) => void;
  onClose?: () => void;
  id?: string;
  isClone?: boolean;
  refetchQueries?: DocumentNode[];
  title?:
    | string
    | (string &
        React.ReactElement<any, string | React.JSXElementConstructor<any>>)
    | (string & Iterable<React.ReactNode>)
    | (string & React.ReactPortal);
}

const getFormData = (
  isClone?: boolean,
  entity?: { name?: string; questions?: { id: any; order?: number }[] | null },
) => {
  const questionnaireName = entity?.name || '';
  const name = isClone ? `${questionnaireName} - clone` : questionnaireName;
  return {
    name: name,
    questions:
      entity?.questions?.map((questionnaireQuestion) => {
        return {
          id: questionnaireQuestion.id,
          order: questionnaireQuestion.order,
        };
      }) || [],
  };
};

export const QuestionnaireForm = ({
  onFinish,
  id,
  refetchQueries,
  isClone,
  onClose,
  title,
}: QuestionnaireFormProps) => {
  const { t } = useTranslation();
  const [displayCancelConfirmation, setDisplayCancelConfirmation] =
    useState(false);

  const [createWttpQuestionnaire, { loading: loadingCreate }] = useMutation(
    CreateWttpQuestionnaireDocument,
    {
      refetchQueries,
    },
  );
  const [updateWttpQuestionnaire, { loading: loadingUpdate }] = useMutation(
    UpdateWttpQuestionnaireDocument,
    {
      refetchQueries,
    },
  );

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors, isDirty },
  } = useForm<Form>({
    defaultValues: {
      name: '',
      questions: [],
    },
  });
  const { entity, loading } = useQuestionnaire({ id });
  useEffect(() => {
    reset(getFormData(isClone, entity as any));
  }, [reset, isClone, entity]);

  const onCancel = () => {
    if (isDirty) {
      setDisplayCancelConfirmation(true);
    } else {
      onClose?.();
    }
  };

  const onSubmit = async (form: Form) => {
    if (!entity?.id || isClone) {
      const result = await createWttpQuestionnaire({
        variables: {
          data: form,
        },
      });
      onFinish?.(result.data?.createWttpQuestionnaire.id);
    } else {
      const result = await updateWttpQuestionnaire({
        variables: {
          data: form,
          id: entity.id,
        },
      });
      onFinish?.(entity.id);
    }
  };

  const handleQuestionsChanged = (
    selectedQuestions: WttpQuestionnaireQuestionDto[],
  ) => {
    setValue(
      'questions',
      [...selectedQuestions]
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((item, index) => {
          return {
            id: item.id,
            order: index + 1,
          } as WttpQuestionnaireQuestionDto;
        }),
      { shouldDirty: true },
    );
  };

  const titleElement =
    title || t('wttp.questionnaire.form.create-questionnaire');

  return (
    <PageModal maxWidth="lg" open onClose={onCancel} title={titleElement}>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <div className={styles.fieldsWrapper}>
          <Controller
            name="name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                label={t('wttp.questionnaire.form.questionnaire-title')}
                placeholder={t('wttp.questionnaire.form.questionnaire-title')}
                error={!!errors.name}
                {...field}
                ref={undefined}
                inputRef={field.ref}
                defaultValue={field.value}
              />
            )}
          />

          <div className={clsx(styles.tableRow, 'mt-4')}>
            <Typography className="flex items-center">
              <List className="mr-2" />
              {t('wttp.questionnaire.form.questions-list')}
            </Typography>
            <Controller
              name="questions"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <WttpQuestionsListForm
                  onChange={handleQuestionsChanged}
                  selectedItems={getValues('questions')}
                  wttpQuestionnaireId={entity?.id}
                  loadingEntity={loading}
                />
              )}
            />
          </div>
        </div>
        <Delimiter className="mt-6" />
        <BottomNavigation>
          <Button
            className="mr-2"
            type="submit"
            variant="contained"
            color="primary"
            size="medium"
            disabled={loadingCreate || loadingUpdate}
          >
            {t('wttp.questionnaire.form.submit-button')}
          </Button>
          <Button variant="outlined" color="primary" onClick={onCancel}>
            {t('common.close')}
          </Button>
        </BottomNavigation>
      </form>
      <FormDirtyModal
        open={displayCancelConfirmation}
        onClose={() => setDisplayCancelConfirmation(false)}
        onCloseWithoutSaving={() => onClose?.()}
      />
    </PageModal>
  );
};
